import type { SubmitEventHandler } from 'react';

import { use, useCallback, useId, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment';
import z from 'zod';

import Button from '../../../components/Button/Button';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { WarningIcon } from '../../../images/shapes';

import { useAppForm } from '../../../form-hooks/form';

const modalParagraphTranslationsKey = {
  userForm: 'ApiKeyGuardModal.Modal.UserFormParagraph',
  spaceForm: 'ApiKeyGuardModal.Modal.SpaceFormParagraph',
};

const modalSubmitButtonKey = {
  userForm: 'ApiKeyGuardModal.Modal.UnassignSpacesButton',
  spaceForm: 'ApiKeyGuardModal.Modal.UnassignUsersButton',
};

const formNotificationKey = {
  userForm: 'ApiKeyGuardModal.Modal.Card.UserFormNotification',
  spaceForm: 'ApiKeyGuardModal.Modal.Card.SpaceFormNotification',
};

export type ParsedApiKeyData = {
  id: string;
  spaceId: string;
  name: string;
  lastUsedAt: string;
};

export interface UsedApiKeyGuardModalContentProps {
  /**
   * Mode of displaying used api key guard modal
   */
  mode: 'userForm' | 'spaceForm';
  /**
   * Subject name of unassigning action (username or space name)
   */
  subject: string;
  /**
   * Target name of unassigning action (usernames or space names)
   */
  target: string;
  /**
   * Parsed api keys data
   */
  apiKeys: ParsedApiKeyData[];
}

export const UsedApiKeyGuardModalContent = ({
  mode,
  subject,
  target,
  apiKeys,
}: UsedApiKeyGuardModalContentProps) => {
  const { t } = useTranslation();
  const formId = useId();
  const modalInstance = use(ModalInstanceContext);

  const validationSchema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          apiKeys.map(({ id }) => [
            id,
            z
              .boolean()
              .refine((value) => value, t('ContentForm.Errors.Required')),
          ]),
        ),
      ),
    [apiKeys, t],
  );

  const form = useAppForm({
    defaultValues: Object.fromEntries(apiKeys.map(({ id }) => [id, false])),
    onSubmit: () => {
      modalInstance.resolve?.(true);
    },
    validators: {
      onChange: validationSchema,
      onMount: validationSchema,
    },
  });

  const handleFormDecline = useCallback(() => {
    modalInstance.resolve?.(false);
  }, [modalInstance]);

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <div className="flex flex-col gap-4">
      <p className="whitespace-pre-line mt-2">
        <Trans
          i18nKey={modalParagraphTranslationsKey[mode]}
          values={{
            subject: subject,
            target: target,
          }}
          components={{
            1: <span className="font-bold"></span>,
          }}
        ></Trans>
      </p>
      <form.AppForm>
        <form id={formId} onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-2 md:gap-4 px-2 mb-4 pb-4 max-h-80 md:max-h-96 overflow-y-scroll">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="p-4 border space-y-4 border-red rounded-md"
              >
                <span className="font-bold flex items-center gap-1 text-red">
                  <WarningIcon className="w-4 h-4" />
                  {t('ApiKeyGuardModal.Modal.Card.Header')}
                </span>
                <p className="whitespace-pre-line">
                  <Trans
                    i18nKey={formNotificationKey[mode]}
                    values={{
                      name: key.name,
                      lastUsedDate: moment(key.lastUsedAt).format(
                        'MMMM DD, YYYY',
                      ),
                    }}
                    components={{
                      1: <span className="font-bold"></span>,
                    }}
                  ></Trans>
                </p>
                <div>
                  <form.AppField name={`${key.id}`}>
                    {(field) => (
                      <field.Checkbox
                        additionalCheckboxClasses="border-slate-200"
                        additionalLabelClasses="after:content-['*'] after:text-red-600 after:inline-block ml-4"
                        label={t('ApiKeyGuardModal.Modal.Card.Agreement')}
                      />
                    )}
                  </form.AppField>
                </div>
              </div>
            ))}
          </div>
          <div
            className="w-full absolute flex justify-center p-4 left-0 bottom-0 border-t
           bg-white gap-2 dark:bg-slate-900 dark:border-slate-700 rounded-b-md"
          >
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
              })}
            >
              {(state) => (
                <Button type="submit" disabled={!state.canSubmit}>
                  {t(modalSubmitButtonKey[mode])}
                </Button>
              )}
            </form.Subscribe>
            <Button buttonColor="blueBordered" onClick={handleFormDecline}>
              {t('Global.Cancel')}
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};
